import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { orderBy } from "lodash";
import {
  getManualSmsTemplates,
  createManualSmsTemplate,
  updateManualSmsTemplate,
  deleteManualSmsTemplate,
} from "../../services/smsTemplates";
import TextInputField from "../Shared/TextInputField";
import Textarea from "components/Shared/Textarea";
import { Skeleton } from "antd";
import PlaceholderHelpLink from "./PlaceholderHelpLink";

const SKELETON_ROW_COUNT = 5;

const ManualSmsTemplates = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tplName, setTplName] = useState("");
  const [tplContent, setTplContent] = useState("");
  const [selectedTplId, setSelectedTplId] = useState("");
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    let mounted = true;
    getManualSmsTemplates()
      .then((data) => {
        if (mounted) setTemplates(data);
      })
      .catch((error) => {
        console.error("Failed to load manual SMS templates", error);
        if (mounted) setTemplates([]);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleClickTemplate = (tpl) => {
    setSelectedTplId(tpl.id);
    setTplName(tpl.name);
    setTplContent(tpl.content);
  };

  const handleClickNew = () => {
    setSelectedTplId("");
    setTplName("");
    setTplContent("");
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (selectedTplId === "") {
      // create new template
      const { id } = await createManualSmsTemplate({ name: tplName, content: tplContent });
      setSelectedTplId(id);
    } else {
      // update existing template
      await updateManualSmsTemplate({ id: selectedTplId, name: tplName, content: tplContent });
    }
    getManualSmsTemplates().then((data) => {
      setTemplates(data);
      setIsSubmitting(false);
    });
  };

  const handleDelete = async () => {
    if (selectedTplId === "") {
      return;
    }
    setIsDeleting(true);
    await deleteManualSmsTemplate({ id: selectedTplId });
    getManualSmsTemplates().then((data) => {
      setTemplates(data);
      setTplName("");
      setSelectedTplId("");
      setTplContent("");
      setIsDeleting(false);
    });
  };

  return (
    <div className="columns">
      <div className="column is-one-third">
        <TableHeader>
          <PlaceholderHelpLink anchor="manual-sms" />
          <NewButton className="button is-small is-radiusless" onClick={handleClickNew}>
            <span className="icon is-small">
              <i className="fas fa-plus"></i>
            </span>
            <span>New</span>
          </NewButton>
        </TableHeader>
        <table className="table is-striped is-hoverable is-size-7">
          <thead>
            <tr>
              <td></td>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                  <tr key={i}>
                    <td>
                      <RowSkeleton active block size="small" />
                    </td>
                  </tr>
                ))
              : orderBy(templates || [], ["name"], ["asc"]).map((tpl) => (
                  <ClickableRow
                    key={tpl.id}
                    className={tpl.id === selectedTplId ? "has-background-info-light" : ""}
                    onClick={() => handleClickTemplate(tpl)}
                  >
                    <td>{tpl.name}</td>
                  </ClickableRow>
                ))}
          </tbody>
        </table>
      </div>
      <div className="column is-two-thirds">
        <div className="columns">
          <div className="column">
            <TextInputField
              label="Name"
              isRequired
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              hasError={!tplName}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <Textarea
              label="Content"
              isRequired
              rows={20}
              value={tplContent}
              onChange={(e) => setTplContent(e.target.value)}
              hasError={!tplContent}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <div className="field is-grouped">
              <p className="control">
                <button
                  className={`${isSubmitting ? "is-loading " : ""}button is-small is-info`}
                  disabled={!tplName || !tplContent || isSubmitting || isDeleting || isLoading}
                  onClick={handleSubmit}
                >
                  {selectedTplId === "" ? "Create" : "Update"}
                </button>
              </p>
              {selectedTplId === "" ? null : (
                <p className="control">
                  <button
                    className={`${isDeleting ? "is-loading " : ""}button is-small is-danger`}
                    disabled={!tplName || !tplContent || isSubmitting || isDeleting || isLoading}
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TableHeader = styled.div`
  display: flex;
  align-items: center;
`;

const NewButton = styled.button`
  margin-left: auto;
`;
const ClickableRow = styled.tr`
  cursor: pointer;
`;
const RowSkeleton = styled(Skeleton.Input)`
  .ant-skeleton-input {
    height: 18px;
  }
`;

export default ManualSmsTemplates;
